import React from 'react';
import { Box, Text, Flex } from '@chakra-ui/react';
import styled from '@emotion/styled';
import dayjs from 'dayjs';
import { CustomTable } from '../../common/tables';
import commafy from 'commafy';

const formatAmount = (amount) => {
  if (amount == null || amount === '' || isNaN(parseFloat(amount))) {
    return null;
  }
  return parseFloat(amount).toFixed(2);
};

const StyledTableWrapper = styled(Box)`
  width: 100%;
  overflow-x: auto;

  table {
    width: 100%;
    border-collapse: collapse;
    border: 1px solid #e5e7eb;
  }

  thead {
    background: #f9fafb;
    border-bottom: 2px solid #e5e7eb;
  }

  th {
    color: #6b7280;
    text-align: left;
    padding: 1rem 1.5rem;
    font-weight: 600;
    font-size: 13px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    white-space: nowrap;
    border-right: 1px solid #e5e7eb;

    &:last-child {
      border-right: none;
    }
  }

  tbody tr {
    border-bottom: 1px solid #e5e7eb;
    transition: background-color 0.2s;

    &:hover {
      background: #f9fafb;
    }

    &:last-child {
      border-bottom: none;
    }
  }

  td {
    padding: 1.25rem 1.5rem;
    color: #374151;
    font-size: 14px;
    white-space: nowrap;
    border-right: 1px solid #e5e7eb;

    &:last-child {
      border-right: none;
    }
  }
`;

const FundingTable = ({ funding }) => {
  if (!funding || funding.length === 0) {
    return (
      <Box bg="white" p="2rem" borderRadius="8px" mb="2rem" boxShadow="md">
        <Text fontSize="20px" fontFamily="bold" color="brand.header" mb="1rem">
          Funding History
        </Text>
        <Box
          p="3rem"
          bg="#F9FAFB"
          borderRadius="8px"
          textAlign="center"
          border="1px dashed #D1D5DB"
        >
          <Text fontSize="16px" color="#6B7280">
            No funding records found for this period.
          </Text>
        </Box>
      </Box>
    );
  }

  const defaultData = funding.map((item) => ({
    col1: item.id,
    col2: item.transaction_type,
    col3: item.amount,
    col4: item.balance_before,
    col5: item.balance_after,
    col6: item.status,
    col7: item.requested_date,
    col8: item.approved_date,
    col9: item.proof_url,
  }));

  const defaultColumns = [
    {
      Header: 'ID',
      accessor: 'col1',
      disableSortBy: true,
      disableFilters: true,
    },
    {
      Header: 'Type',
      accessor: 'col2',
      Cell: ({ value }) => {
        const lowerValue = (value || '').toLowerCase();
        return (
          <Text
            fontFamily="medium"
            color={lowerValue === 'credit' ? '#2EAE65' : '#E24D4D'}
            textTransform="capitalize"
          >
            {value}
          </Text>
        );
      },
      disableSortBy: true,
      disableFilters: true,
    },
    {
      Header: 'Amount',
      accessor: 'col3',
      Cell: ({ value }) => {
        const formatted = formatAmount(value);
        return (
          <Text fontFamily="medium">
            {formatted != null ? `₦${commafy(formatted)}` : 'N/A'}
          </Text>
        );
      },
      disableFilters: true,
    },
    {
      Header: 'Balance Before',
      accessor: 'col4',
      Cell: ({ value }) => {
        const formatted = formatAmount(value);
        return (
          <Text fontFamily="medium">
            {formatted != null ? `₦${commafy(formatted)}` : 'N/A'}
          </Text>
        );
      },
      disableFilters: true,
    },
    {
      Header: 'Balance After',
      accessor: 'col5',
      Cell: ({ value }) => {
        const formatted = formatAmount(value);
        return (
          <Text fontFamily="medium">
            {formatted != null ? `₦${commafy(formatted)}` : 'N/A'}
          </Text>
        );
      },
      disableFilters: true,
    },
    {
      Header: 'Status',
      accessor: 'col6',
      Cell: ({ value }) => (
        <Text
          fontFamily="medium"
          color={
            value === 'approved'
              ? '#2EAE65'
              : value === 'pending'
              ? '#FFA500'
              : '#E24D4D'
          }
          textTransform="capitalize"
        >
          {value}
        </Text>
      ),
      disableFilters: true,
    },
    {
      Header: 'Requested Date',
      accessor: 'col7',
      Cell: ({ value }) => (
        <Text fontFamily="regular">
          {dayjs(value).format('MMM D, YYYY')}
          <Text as="span" ml="10px" color="#808080">
            {dayjs(value).format('hh:mm A')}
          </Text>
        </Text>
      ),
      disableFilters: true,
    },
    {
      Header: 'Approved Date',
      accessor: 'col8',
      Cell: ({ value }) =>
        value ? (
          <Text fontFamily="regular">
            {dayjs(value).format('MMM D, YYYY')}
            <Text as="span" ml="10px" color="#808080">
              {dayjs(value).format('hh:mm A')}
            </Text>
          </Text>
        ) : (
          <Text color="#808080">-</Text>
        ),
      disableFilters: true,
    },
    {
      Header: 'Proof',
      accessor: 'col9',
      Cell: ({ value }) =>
        value ? (
          <a
            href={value}
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: '#1A73E8', textDecoration: 'underline' }}
          >
            View
          </a>
        ) : (
          <Text color="#808080">-</Text>
        ),
      disableFilters: true,
    },
  ];

  return (
    <Box bg="white" p="2rem" borderRadius="8px" mb="2rem" boxShadow="md">
      <Flex justifyContent="space-between" alignItems="center" mb="2rem">
        <Text fontSize="20px" fontFamily="bold" color="brand.header">
          Funding History
        </Text>
        <Box
          bg="#E8F5E9"
          px="1rem"
          py="0.5rem"
          borderRadius="20px"
          border="1px solid"
          borderColor="#2EAE65"
        >
          <Text fontSize="14px" fontFamily="bold" color="#2EAE65">
            {funding.length} {funding.length === 1 ? 'Record' : 'Records'}
          </Text>
        </Box>
      </Flex>
      <StyledTableWrapper>
        <CustomTable
          columns={defaultColumns}
          data={defaultData}
          showPagination={false}
        />
      </StyledTableWrapper>
    </Box>
  );
};

export { FundingTable };
